<template>
  <div>
    <nav-bar />
    <v-container class="mt-10 pt-5">
      <!-- Page Header ########################################### -->
      <header-page
        titleText="Gestão de Secretarias"
        titleIcon="fal fa-users-class"
      />

      <v-container pa-6>
        <v-row justify="space-between">
          <v-col cols="auto">
            <p class="list-labels">Lista de Secretarias</p>
          </v-col>
          <v-skeleton-loader
            ref="skeleton"
            :boilerplate="false"
            type="table-tfoot"
            :tile="false"
            class="ml-auto"
            v-show="loading"
            min-width="300px"
          ></v-skeleton-loader>
          <v-col cols="auto" v-show="!loading">
            <v-container grid-list-md pa-0>
              <v-layout justify-start wrap justify-sm-end>
                <v-flex col-12 col-sm-6>
                   <v-combobox
                    v-model="currentSearchTerm"
                    :loading="loading"
                    :items="items"
                    label="Buscar"
                    outlined
                    clearable
                    dense
                    @change="addSearchTerm($event, true)"        
                  ></v-combobox>
                </v-flex>
                <v-flex flex-grow-0 col-12 col-sm-4>
                  <v-select
                    v-model="selectedOrderBy"
                    :items="orderByOptions"
                    label="Ordenar por"
                    item-text="name"
                    item-value="id"
                    outlined
                    hide-details="auto"
                    dense
                    solo
                    flat
                    @change="loadSecretaries"
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-container>
          </v-col>
        </v-row>
      </v-container>

      <!-- Search Results ########################################### -->
      <search-result
        inset
        :totalResults="totalNumberOfSecretaries"
        v-model="searchTerms"
        @itemRemoved="searchTermRemoved()"
        :addButtonDestiny="routes.app.SecretarysRegisterNew"
      />

      <v-skeleton-loader
        ref="skeleton"
        :boilerplate="false"
        type="table-tbody"
        :tile="false"
        class="mx-auto"
        v-show="loading"
      ></v-skeleton-loader>

      <!-- Page Body ########################################### -->
      <v-container>
        <div>
          <template v-for="(secretary, index) in secretaries">
            <v-divider v-if="index > 0" :key="'Secretary' + index"></v-divider>
            <v-layout :key="secretary.id" class="main-list-item" align-center>
              <!-- dados -->
              <v-col cols="12" md="6">
                <v-flex pl-2 pt-sm-0 pt-12>
                  <span>{{ secretary.entityViewModel.socialReason }}</span>
                  <h4 class="font-weight-regular">
                    {{ secretary.entityViewModel.name }}
                    <span class="caption op-5">
                      ({{
                        secretary.entityViewModel.document | FormatDocument
                      }})</span
                    >
                  </h4>
                </v-flex>
              </v-col>

              <!-- tools #########################-->
              <div class="tools">
                <v-container fluid pa-0>
                  <v-row no-wrap align="center" no-gutters>
                    <!-- creation date  #########################-->
                    <v-col cols="auto">
                      <v-layout align-end column>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-flex>
                              <v-icon
                                color="var(--text)"
                                class="mr-2"
                                size="16"
                                v-on="on"
                                >fal fa-user-tag</v-icon
                              >
                              <!-- <span class="tiny-label">{{secretary.id}}</span> -->
                              <span class="tiny-label">{{
                                GetSecretaryAge(secretary.entityViewModel)
                              }}</span>
                            </v-flex>
                          </template>
                          <span
                            >Cadastrado em:
                            {{
                              secretary.entityViewModel.creationDate
                                | FormatDate
                            }}</span
                          >
                        </v-tooltip>
                      </v-layout>
                    </v-col>

                    <!-- context menu - mobile #########################-->
                    <v-menu bottom left>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          v-on="on"
                          color="var(--gray)"
                          class="ml-3 d-flex d-md-none"
                        >
                          <v-icon>far fa-ellipsis-v</v-icon>
                        </v-btn>
                      </template>

                      <v-list dense>
                        <v-list-item v-for="(item, i) in contextMenu" :key="i">
                        </v-list-item>
                      </v-list>
                    </v-menu>

                    <!-- edit & on/off #########################-->
                    <v-col cols="auto">
                      <v-layout
                        align-center
                        class="d-none d-md-flex flex-grow-0"
                      >
                        <v-flex pl-6>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                fab
                                small
                                color="var(--gray)"
                                class="hover-red"
                                v-on="on"
                                @click="editSecretary(secretary)"
                              >
                                <v-icon size="20">fal fa-pen</v-icon>
                              </v-btn>
                            </template>
                            <span>Editar</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex pl-6>
                          <v-switch
                            v-model="secretary.entityViewModel.active"
                            @change="
                              updateSecretaryStatus(
                                secretary.entityViewModel.id
                              )
                            "
                            inset
                          >
                          </v-switch>
                        </v-flex>
                      </v-layout>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
            </v-layout>
          </template>
        </div>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import NavBar from "@/components/NavBar";
import HeaderPage from "@/components/HeaderPage";
import SearchResult from "@/components/SearchResult";
import EntityService from "@/scripts/services/entity.service";
import Routes from "@/router/names.js";
import SecretaryOrderByOptions from "@/scripts/enums/secretaryOrderByOptions.enum";
import CommonHelper from "@/scripts/helpers/common.helper";

export default {
  name: "SecretarysManagement",
  components: {
    NavBar,
    HeaderPage,
    SearchResult
  },
  data: () => ({
    routes: Routes,
    entityService: new EntityService(),
    loadingCount: false,
    loadingList: false,
    secretaries: [],
    items: [],
    totalNumberOfSecretaries: 0,
    selectedSecretary: null,
    page: 1,
    dialog: false,
    searchTerms: [],
    searchWaitTime: 2000,
    selectedOrderBy: SecretaryOrderByOptions.Name,
    currentSearchTerm: "",
    orderByOptions: [
      { id: SecretaryOrderByOptions.CreationDate, name: "Ordenar por data" },
      { id: SecretaryOrderByOptions.Name, name: "Ordenar por nome" },
      { id: SecretaryOrderByOptions.Status, name: "Ordenar por status" }
    ],
    contextMenu: [
      { icon: "fal fa-pen", menu: "Editar" },
      { icon: "fal fa-ban", menu: "Desativar" }
    ]
  }),
  mounted() {
    this.loadSecretaries();
  },
  computed: {
    loading() {
      return this.loadingList || this.loadingCount;
    }
  },
  methods: {
    ...mapActions(["setSelectedSecretary"]),
    getFilters() {
      return {
        orderBy: this.selectedOrderBy,
        currentPage: this.currentPage - 1,
        pageSize: this.selectedPagination,
        searchTerms: this.searchTerms
      };
    },
    loadSecretaries() {
      if (this.loading) return;
      this.loadingCount = true;
      this.loadingList = true;
      this.searchTimeout = null;
      this.entityService
        .countSecretaries(this.getFilters())
        .then(this.countSecretariesCallBack)
        .finally(() => (this.loadingCount = false));
      this.entityService
        .listSecretaries(this.getFilters())
        .then(this.listSecretariesCallBack)
        .finally(() => (this.loadingList = false));
    },
    listSecretariesCallBack(data) {
      this.secretaries = data;
      if(this.items.length == 0) {
        this.secretaries.forEach(element => {
          this.items.push(element.entityViewModel.name);
        });
        this.item = this.items.sort();
      }
      this.loadingList = false;
    },
    countSecretariesCallBack(data) {
      this.totalNumberOfSecretaries = data;
      this.loadingCount = false;
    },
    editSecretary(secretary) {
      this.setSelectedSecretary(secretary);
      this.$router.push({
        name: Routes.app.SecretarysRegister,
        params: { secretaryId: secretary.id }
      });
    },
    updateSecretaryStatus(entityId) {
      this.entityService
        .updateEntityStatus(entityId);
    },
    searchTermRemoved() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(
        this.loadSecretaries,
        this.searchWaitTime
      );
    },
    addSearchTerm(event, addAnyway) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(this.loadSecretaries, this.searchWaitTime);
      }
      if (event.key == "Enter" || addAnyway) {
        if (
          this.currentSearchTerm &&
          this.currentSearchTerm.length > 0 &&
          this.searchTerms.indexOf(this.currentSearchTerm) == -1
        )
          this.searchTerms.push(this.currentSearchTerm);
        this.currentSearchTerm = [];
        if (!this.searchTimeout)
          this.searchTimeout = setTimeout(this.loadSecretaries, this.searchWaitTime);
      }
    },
    GetSecretaryAge(entity) {
      return CommonHelper.GetEntityAge(entity);
    }
  }
};
</script>